import { Link } from 'react-router-dom'
import { ArrowRight, Trophy, Users, Target, Award, Heart, Sparkles, Handshake, MessageCircle, Star, Palette } from 'lucide-react'
import { useClub, useCoaches } from '@/lib/hooks'
import { Reveal } from '@/components/motion/Reveal'
import { PageHero } from '@/components/layout/PageHero'
import { Button } from '@/components/ui/button'

const VALUE_ICONS: Record<string, React.ReactNode> = {
  Sportsmanship: <Trophy className="h-5 w-5" />,
  'Community Relationships': <Users className="h-5 w-5" />,
  Excellence: <Award className="h-5 w-5" />,
  Positivity: <Heart className="h-5 w-5" />,
  Leadership: <Star className="h-5 w-5" />,
  Communication: <MessageCircle className="h-5 w-5" />,
  Respect: <Handshake className="h-5 w-5" />,
  Creativity: <Sparkles className="h-5 w-5" />,
  Diversity: <Palette className="h-5 w-5" />,
  Partnerships: <Target className="h-5 w-5" />,
}

const IMAGES = {
  hero: 'https://cosmosfc.org/wp-content/uploads/2022/03/jj07-1024x682.jpg',
  mission: 'https://cosmosfc.org/wp-content/uploads/2022/03/S6-1024x670.jpg',
  vision: 'https://cosmosfc.org/wp-content/uploads/2022/03/CM8-1.jpg',
  training: 'https://cosmosfc.org/wp-content/uploads/2022/03/REC1-1024x768.jpg',
  match: 'https://cosmosfc.org/wp-content/uploads/2022/03/Z4-1024x768.jpg',
  jamaica: 'https://cosmosfc.org/wp-content/uploads/2026/04/new-683x1024.jpeg',
  coach: 'https://cosmosfc.org/wp-content/uploads/2022/03/C2-3.jpg',
  spain: 'https://cosmosfc.org/wp-content/uploads/2022/03/jj01-1024x352.jpg',
  gallery: [
    'https://cosmosfc.org/wp-content/uploads/2022/03/Z1-1.jpg',
    'https://cosmosfc.org/wp-content/uploads/2022/03/Z3.jpg',
    'https://cosmosfc.org/wp-content/uploads/2022/03/jj02.jpg',
    'https://cosmosfc.org/wp-content/uploads/2022/03/CFCTS.jpg',
    'https://cosmosfc.org/wp-content/uploads/2022/03/S8.jpg',
    'https://cosmosfc.org/wp-content/uploads/2022/03/jj06.jpg',
  ],
}

export function AboutPage() {
  const { data: club } = useClub()
  const { data: coaches = [] } = useCoaches()
  const dol = coaches.find((c) => c.slug === 'ras-charles-lawrence')

  if (!club) return null

  return (
    <>
      <PageHero
        eyebrow="About Cosmos F.C."
        title="Our Story."
        intro={`${club.affiliation}. A ${club.status} youth soccer club based in ${club.address.city}, ${club.address.state}, developing players from ages 4 through 18.`}
        image={IMAGES.hero}
        crumbs={[{ label: 'About' }]}
        height="tall"
      />

      {/* Intro band with founding year + status */}
      <section className="py-16 bg-muted/40">
        <div className="container-narrow grid gap-6 sm:grid-cols-3 text-center">
          <div>
            <div className="font-display text-5xl text-cosmos-green num">{club.since}</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mt-2">Founded</div>
          </div>
          <div>
            <div className="font-display text-5xl text-cosmos-gold-dark">501(c)(3)</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mt-2">Non-Profit Status</div>
          </div>
          <div>
            <div className="font-display text-3xl md:text-4xl leading-tight">Vista · California</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mt-2">Home Base</div>
          </div>
        </div>
      </section>

      {/* Mission — text left, image right */}
      <section id="mission" className="py-24">
        <div className="container-narrow grid gap-12 md:grid-cols-[1fr_1fr] items-center">
          <Reveal>
            <span className="eyebrow">Mission</span>
            <h2 className="mt-3 font-display text-display-3 uppercase leading-none">Developing well-rounded individuals.</h2>
            <p className="mt-6 text-base text-muted-foreground leading-relaxed">{club.mission}</p>
            <blockquote className="mt-6 border-l-4 border-cosmos-green pl-4 italic text-cosmos-green">
              "{club.quotes[0]}"
            </blockquote>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden">
              <img src={IMAGES.mission} alt="Cosmos F.C. training" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-pitch-950/60 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Vision — image left, text right (dark) */}
      <section id="vision" className="py-24 bg-pitch-900 text-white">
        <div className="container-narrow grid gap-12 md:grid-cols-[1fr_1fr] items-center">
          <Reveal>
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden">
              <img src={IMAGES.vision} alt="Cosmos F.C. match" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-pitch-950/50 to-transparent" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="eyebrow eyebrow-gold">Vision</span>
            <h2 className="mt-3 font-display text-display-3 uppercase leading-none">One of the best programs in youth soccer.</h2>
            <p className="mt-6 text-base text-white/75 leading-relaxed">{club.vision}</p>
            <blockquote className="mt-6 border-l-4 border-cosmos-gold pl-4 italic text-cosmos-gold">
              "{club.quotes[1]}"
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24">
        <div className="container-narrow">
          <div className="mb-12 text-center">
            <span className="eyebrow">What we stand for</span>
            <h2 className="mt-3 font-display text-display-3 uppercase">Core Values.</h2>
            <p className="mt-4 max-w-2xl mx-auto text-muted-foreground">Ten principles that define how Cosmos F.C. operates on and off the pitch.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-5">
            {club.coreValues.map((v, i) => (
              <Reveal key={v} delay={i * 0.04}>
                <div className="h-full rounded-xl border border-border bg-card p-5 hover:border-cosmos-green hover:-translate-y-1 transition-all">
                  <div className="text-cosmos-green mb-3">{VALUE_ICONS[v] ?? <Star className="h-5 w-5" />}</div>
                  <div className="font-display text-cosmos-gold-dark text-xs num mb-1">{String(i + 1).padStart(2, '0')}</div>
                  <div className="font-display text-base uppercase leading-tight">{v}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Director spotlight */}
      {dol && (
        <section className="py-24 bg-muted/40">
          <div className="container-narrow">
            <div className="mb-8 text-center">
              <span className="eyebrow">Director of Coaching</span>
              <h2 className="mt-3 font-display text-display-3 uppercase">Coach Charles Lawrence.</h2>
            </div>
            <div className="grid gap-8 md:grid-cols-[1fr_1.4fr] items-start rounded-2xl border border-border bg-card p-8">
              <Reveal>
                <div className="rounded-xl overflow-hidden aspect-[4/5] bg-pitch-800">
                  <img src={IMAGES.coach} alt="Coach Charles Lawrence" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="text-xs uppercase tracking-widest text-cosmos-green font-bold">Jamaica · USSF · CONCACAF · AC Milan / Real Madrid / Atletico Madrid Trainer</div>
                <h3 className="mt-2 font-display text-3xl uppercase">{dol.name}</h3>
                <div className="mt-4 space-y-3 text-sm text-muted-foreground leading-relaxed">
                  {dol.bio.split('\n\n').map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
                {dol.quote && (
                  <blockquote className="mt-6 border-l-4 border-cosmos-gold bg-cosmos-gold/8 pl-4 py-3 pr-3 italic text-cosmos-gold-dark">
                    "{dol.quote}" <span className="not-italic block mt-1 text-xs font-bold uppercase tracking-widest">— {dol.name}</span>
                  </blockquote>
                )}
                <Button asChild className="mt-6"><Link to="/coaches">Meet the full staff <ArrowRight className="h-4 w-4" /></Link></Button>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* Global reach: Spain + Jamaica */}
      <section className="py-24">
        <div className="container-narrow">
          <div className="mb-10 text-center">
            <span className="eyebrow eyebrow-gold justify-center">Global reach</span>
            <h2 className="mt-3 font-display text-display-3 uppercase">Cosmos beyond Vista.</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            <Reveal>
              <Link to="/programs/international-tours" className="block group rounded-2xl overflow-hidden border border-border bg-card hover:shadow-lg transition-all">
                <div className="aspect-video overflow-hidden bg-pitch-800">
                  <img src={IMAGES.spain} alt="Spain tour" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="text-xs uppercase tracking-widest text-cosmos-green font-bold">Spain · LALIGA</div>
                  <div className="font-display text-2xl uppercase mt-1">Canary Football Academy</div>
                  <p className="text-sm text-muted-foreground mt-2">Selected squads travel to Spain each year to train and compete against LALIGA 2nd & 3rd Division youth teams.</p>
                </div>
              </Link>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/programs/jamaica" className="block group rounded-2xl overflow-hidden border border-border bg-card hover:shadow-lg transition-all">
                <div className="aspect-video overflow-hidden bg-pitch-800">
                  <img src={IMAGES.jamaica} alt="Jamaica program" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="text-xs uppercase tracking-widest text-cosmos-gold-dark font-bold">Jamaica · 6 Regional Coaches</div>
                  <div className="font-display text-2xl uppercase mt-1">Jamaica Development Program</div>
                  <p className="text-sm text-muted-foreground mt-2">Kingston, St. Ann, St. Catherine, St. Mary — teaching the game the right way with fun, learning, and personal growth.</p>
                </div>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Photo grid */}
      <section className="py-24 bg-muted/40">
        <div className="container-narrow">
          <div className="mb-8 text-center">
            <span className="eyebrow justify-center">Moments</span>
            <h2 className="mt-3 font-display text-display-3 uppercase">In pictures.</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {IMAGES.gallery.map((src, i) => (
              <div key={src} className={`rounded-xl overflow-hidden aspect-[4/3] bg-pitch-800 ${i === 0 ? 'md:col-span-2 md:row-span-2 md:aspect-square' : ''}`}>
                <img src={src} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-pitch-950 text-white">
        <div className="container-narrow text-center">
          <span className="eyebrow eyebrow-gold justify-center">Join us</span>
          <h2 className="mt-3 font-display text-display-3 uppercase">Become part of Cosmos F.C.</h2>
          <p className="mt-4 text-white/70 max-w-xl mx-auto">Registration is open for all age groups. A coach reaches out within 48 hours to confirm placement.</p>
          <div className="mt-6 flex gap-3 justify-center flex-wrap">
            <Button asChild variant="gold"><Link to="/register">Register a Player <ArrowRight className="h-4 w-4" /></Link></Button>
            <Button asChild variant="outline" className="border-white/30 text-white hover:bg-white/10"><Link to="/contact">Contact Office</Link></Button>
          </div>
        </div>
      </section>
    </>
  )
}
